// Godkjennings-arbeidsflyt for ildmål (kill-chain-gate). Doktrinært krever visse
// ildoppdrag EKSPLISITT kommandant-godkjenning før skudd: DANGER CLOSE (egne innen
// fareavstand) og betydelig CDE (utilsiktet skade mot beskyttede objekter). Denne
// modulen avgjør NÅR godkjenning kreves, og håndhever at et skudd bare kan
// frigis med en REELL, menneskesignert godkjenning — aldri auto-stemplet.
//
// Lagdeling (grunnlov §4): denne rene logikken sier «kreves / kan frigis». At
// GODKJENNEREN faktisk HAR kommando-myndighet håndheves autoritativt i hub-en
// (RBAC/echelon) — contracts avhenger aldri av rbac.
import { z } from 'zod';
/** Hvorfor eskalert godkjenning kreves. */
export const ApprovalReason = z.enum(['danger-close', 'cde-hoy', 'cde-moderat']);
export const APPROVAL_REASON_LABEL = {
    'danger-close': 'DANGER CLOSE (egne innen fareavstand)',
    'cde-hoy': 'CDE Høy (beskyttet objekt svært nær)',
    'cde-moderat': 'CDE Moderat (beskyttet objekt nær)',
};
/**
 * Avgjør om et ildmål krever eskalert kommandant-godkjenning.
 * Utløsere: DANGER CLOSE og CDE moderat/høy. (TST/HPT påvirker hastegrad, ikke
 * selve godkjennings-kravet — tidskritisk ild frigis fortsatt av et menneske.)
 */
export function assessApprovalNeed(input) {
    const reasons = [];
    if (input.dangerClose?.declareDangerClose)
        reasons.push('danger-close');
    if (input.cde?.level === 'hoy')
        reasons.push('cde-hoy');
    else if (input.cde?.level === 'moderat')
        reasons.push('cde-moderat');
    return { required: reasons.length > 0, reasons };
}
export const ApprovalDecision = z.enum(['approved', 'denied']);
/**
 * En menneskesignert godkjennings-post. `approverInitials` er signaturen og MÅ
 * være ikke-tom — dette er vernet mot auto-stempling: uten reelle initialer
 * validerer ikke posten, og skuddet kan ikke frigis.
 */
export const FireApprovalSchema = z.object({
    missionId: z.string().min(1).max(64),
    approverRole: z.string().min(1).max(64),
    approverInitials: z.string().trim().min(2).max(8),
    decision: ApprovalDecision,
    reasons: z.array(ApprovalReason).default([]),
    note: z.string().max(300).optional(),
    tsMs: z.number(),
});
/** Coerce vilkårlig input til en gyldig godkjennings-post, eller null. */
export function parseFireApproval(raw) {
    const r = FireApprovalSchema.safeParse(raw);
    return r.success ? r.data : null;
}
/**
 * Frigivnings-gaten: kan dette ildmålet skytes?
 * - Krever ikke godkjenning → ok.
 * - Krever godkjenning → KUN med en gyldig post (decision=approved, reelle
 *   initialer) for RIKTIG oppdrag. Manglende/nektet/feil-oppdrag → nektet.
 * Fabrikkerer ALDRI en godkjenning; kaller må levere en reell post.
 */
export function canRelease(need, missionId, approval) {
    if (!need.required)
        return { ok: true };
    if (!approval) {
        return { ok: false, reason: `Krever kommandant-godkjenning: ${need.reasons.map((r) => APPROVAL_REASON_LABEL[r]).join('; ')}` };
    }
    if (approval.missionId !== missionId) {
        return { ok: false, reason: 'Godkjenning gjelder et annet oppdrag' };
    }
    if (approval.decision === 'denied') {
        return { ok: false, reason: `Ild NEKTET av ${approval.approverRole} (${approval.approverInitials})` };
    }
    return { ok: true };
}
/** Menneskelesbar signaturlinje for logg/COP. */
export function approvalMarking(a) {
    const verb = a.decision === 'approved' ? 'GODKJENT' : 'NEKTET';
    return `${verb} av ${a.approverRole} (${a.approverInitials})`;
}
//# sourceMappingURL=fireapproval.js.map